import { clsx } from 'clsx';
import { ChevronDown } from 'lucide-react';
import { Accordion as AccordionPrimitive } from 'radix-ui';
import type { ComponentPropsWithoutRef } from 'react';

/** Radix Accordion, bespoke-styled: hairline rows, a chevron that turns, content that rises 4 px as it fades in. */
export function Accordion({
  className,
  ...props
}: ComponentPropsWithoutRef<typeof AccordionPrimitive.Root>) {
  return <AccordionPrimitive.Root className={clsx('accordion', className)} {...props} />;
}

export function AccordionItem({
  className,
  ...props
}: ComponentPropsWithoutRef<typeof AccordionPrimitive.Item>) {
  return <AccordionPrimitive.Item className={clsx('accordion__item', className)} {...props} />;
}

export function AccordionTrigger({
  className,
  children,
  headingLevel = 3,
  ...props
}: ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger> & {
  /** Semantic level of the row's heading: one below the heading that introduces the accordion. */
  headingLevel?: 2 | 3 | 4;
}) {
  const Heading = `h${String(headingLevel)}` as 'h2' | 'h3' | 'h4';
  return (
    <AccordionPrimitive.Header asChild>
      <Heading className="accordion__header">
        <AccordionPrimitive.Trigger className={clsx('accordion__trigger', className)} {...props}>
          <span>{children}</span>
          <ChevronDown className="accordion__chevron" size={18} aria-hidden="true" />
        </AccordionPrimitive.Trigger>
      </Heading>
    </AccordionPrimitive.Header>
  );
}

export function AccordionContent({
  className,
  children,
  ...props
}: ComponentPropsWithoutRef<typeof AccordionPrimitive.Content>) {
  return (
    <AccordionPrimitive.Content className={clsx('accordion__content', className)} {...props}>
      <div className="accordion__body">{children}</div>
    </AccordionPrimitive.Content>
  );
}
